import React, { Component } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { Images } from "../../assets/images/splatter_imgs";
import { Splatter } from "./Splatter";
import { normalizeSectionOrder } from "./sectionOrder";
import DEFAULT_HOME_DOCUMENT from "./home_content.default.json";
import "./Home.css";

class Home extends Component {
  state = {
    // Rendered content comes from the published document. We start from the
    // baked-in default so the page renders instantly and never breaks if the
    // API is unavailable; the fetch below replaces it with the live content.
    document: DEFAULT_HOME_DOCUMENT,
    activeState: "apply",
    currSession: "",
    selectedLeader: null,
    selectedProject: null,
  };

  componentDidMount = () => {
    // Preview mode: render a caller-provided document instead of fetching.
    if (this.props.overrideDocument) {
      this.setState(
        { document: this.props.overrideDocument, activeState: this.props.overrideActiveState || "apply" },
        this.generateSplatters,
      );
      return;
    }
    axios
      .get("/api/home-content")
      .then((res) => {
        const document = res.data && res.data.document ? res.data.document : DEFAULT_HOME_DOCUMENT;
        const activeState = res.data && res.data.activeState ? res.data.activeState : "apply";
        this.setState({ document, activeState }, this.generateSplatters);
      })
      .catch(() => {
        // API unavailable — keep the baked-in default document.
        this.setState({ document: DEFAULT_HOME_DOCUMENT, activeState: "apply" }, this.generateSplatters);
      });
  };

  componentDidUpdate = (prevProps) => {
    // Preview mode: re-render when the caller supplies an updated draft document OR changes
    // the previewed call-to-action state (e.g. the "Preview as" selector). Guard so we only
    // setState on an actual change, avoiding an update loop.
    if (
      this.props.overrideDocument &&
      (this.props.overrideDocument !== prevProps.overrideDocument ||
        this.props.overrideActiveState !== prevProps.overrideActiveState)
    ) {
      this.setState(
        { document: this.props.overrideDocument, activeState: this.props.overrideActiveState || "apply" },
        this.generateSplatters,
      );
    }
  };

  /**
   * Builds the artistic splatter/overlay treatment. The texture and mask sprites
   * remain fixed bundled art direction; only the five section OVERLAY images are
   * driven by the (editable) document.
   */
  generateSplatters = () => {
    const doc = this.state.document || DEFAULT_HOME_DOCUMENT;
    const body = document.querySelector("body");

    const heroEl = document.querySelector("#about-splatter");
    if (!heroEl) return;
    heroEl.innerHTML = "";

    var splatter = new Splatter("#about-splatter", {
      width: body.clientWidth,
      height: window.innerHeight,
      maskSprites: [
        {
          count: Math.min(Math.round(body.clientWidth / 600) * 4, 18),
          url: [Images["splatter_hard_1"], Images["splatter_hard_2"], Images["splatter_hard_3"]],
          offset: [0.75, 0.15],
          scale: [0.5, 0.5],
          rotate: [360, 0],
          opacity: 0.15,
        },
        {
          count: Math.min(Math.round(body.clientWidth / 600) * 4, 18),
          url: [Images["splatter_soft_1"], Images["splatter_soft_2"], Images["splatter_soft_3"]],
          offset: [0.75, 0.15],
          scale: [0.65, 1],
          rotate: [360, 0],
          opacity: 0.1,
        },
        {
          count: 1,
          url: Images["splatter_layer"],
          offset: [0, 0],
          scale: 1,
          rotate: 0,
          opacity: 0.75,
        },
      ],
    });
    splatter.generate();

    // Section overlays come from the document (editable), in the officer-defined
    // order. The four middle sections are driven by `sectionOrder`; the Join Us
    // ("apply") splatter is pinned last, mirroring the render order.
    var order = normalizeSectionOrder(doc.sectionOrder);
    var overlayByKey = {
      mission: doc.mission && doc.mission.imageKey,
      sessions: doc.sessions && doc.sessions.imageKey,
      projects: doc.projects && doc.projects.imageKey,
      leaders: doc.leaders && doc.leaders.imageKey,
      apply: doc.joinUs && doc.joinUs.imageKey,
    };
    var sections = [...order, "apply"];
    var overlays = sections.map((key) => overlayByKey[key]);

    var sessionsEl = document.getElementById("sessions-splatter");
    var contHeight = sessionsEl ? sessionsEl.clientHeight : 0;
    for (var i = 0; i < sections.length; i++) {
      let target = document.getElementById(sections[i] + "-splatter");
      if (!target) continue;
      target.querySelectorAll("svg").forEach((el) => el.remove());

      // Most sections use an oversized, deliberately-bleeding abstract splatter.
      // The Mission section shows a real photo, so keep it inside its own right-hand
      // column (its container box) rather than letting it spill under the text.
      let isMission = sections[i] === "mission";
      let sectionWidth = isMission
        ? Math.min(target.clientWidth || body.clientWidth * 0.5, 1000)
        : Math.min(body.clientWidth * 1.2, 1920);

      let options = {
        width: sectionWidth,
        height: Math.min(contHeight, 1080),
        texture: { url: Images["background_texture"], rotate: 0 },
        overlay: overlays[i],
        maskSprites: [
          {
            count: Math.min(Math.round(body.clientWidth / 300) * 2, 8),
            url: [Images["splatter_hard_1"], Images["splatter_hard_2"], Images["splatter_hard_3"]],
            offset: body.clientWidth >= 1000 ? [0.25, 0.5] : [0.75, 0.05],
            scale: body.clientWidth >= 800 ? [0.5, 0.75] : [0.5, 0.5],
            rotate: [360, 0],
            opacity: 0.3,
          },
          {
            count: Math.min(Math.round(body.clientWidth / 300) * 2, 8),
            url: [Images["splatter_soft_1"], Images["splatter_soft_2"], Images["splatter_soft_3"]],
            offset: body.clientWidth >= 1000 ? [0.25, 0.5] : [0.75, 0.05],
            scale: body.clientWidth >= 800 ? [0.65, 0.75] : [0.65, 0.5],
            rotate: [360, 0],
            opacity: 0.3,
          },
        ],
      };

      let s = new Splatter("#" + sections[i] + "-splatter", options);
      s.generate();
    }
  };

  selectProject = (i) => {
    this.setState((s) => ({ selectedProject: s.selectedProject === i ? null : i }));
  };

  selectLeader = (i) => {
    this.setState((s) => ({ selectedLeader: s.selectedLeader === i ? null : i }));
  };

  startVideo = () => {
    var aboutVideo = document.getElementById("about-video");
    aboutVideo.classList.remove("hidden");
    aboutVideo.querySelector("video").currentTime = 0;
    aboutVideo.querySelector("video").play();
  };

  stopVideo = (e) => {
    e.stopPropagation();
    var aboutVideo = document.getElementById("about-video");
    aboutVideo.classList.add("hidden");
    aboutVideo.querySelector("video").pause();
  };

  setSession = (session) => {
    // Toggle: clicking the open row collapses it back so the list can return to
    // the all-collapsed state; clicking a different row swaps the open one.
    this.setState((prev) => ({ currSession: prev.currSession === session ? "" : session }));
  };

  render() {
    const doc = this.state.document || DEFAULT_HOME_DOCUMENT;
    const hero = doc.hero || {};
    const mission = doc.mission || {};
    const sessions = doc.sessions || {};
    const projects = doc.projects || {};
    const leaders = doc.leaders || {};
    const joinUs = doc.joinUs || {};
    const sessionItems = Array.isArray(sessions.items) ? sessions.items : [];
    const projectItems = Array.isArray(projects.items) ? projects.items : [];
    const leaderItems = Array.isArray(leaders.items) ? leaders.items : [];
    const activeState = this.state.activeState;
    const activeMessage = joinUs.messages ? joinUs.messages[activeState] : "";
    const sectionOrder = normalizeSectionOrder(doc.sectionOrder);

    // The four middle sections render between the fixed Hero and Join Us bookends
    // in the officer-defined `sectionOrder`. Each section's markup is unchanged;
    // only its position in the sequence is driven by the order.
    const sectionRenderers = {
      mission: () => (
        <React.Fragment key="mission">
          <span id="mission"></span>
          <section className="section section--mission">
            <div className="section-content">
              <h1 className="section-title">{mission.heading || "Mission"}</h1>
              {mission.tagline ? <p>{mission.tagline}</p> : null}
              <div dangerouslySetInnerHTML={{ __html: mission.body || "" }} />
            </div>

            <div
              id="mission-splatter"
              className="section-image"
              style={{ "--splatter-opacity": mission.imageOpacity ?? 1 }}
            >
              <blockquote className="section-quote">
                {mission.quote}
                <span className="section-author"></span>
              </blockquote>
            </div>
          </section>
        </React.Fragment>
      ),
      sessions: () => (
        <React.Fragment key="sessions">
          <span id="sessions"></span>
          <section className="section">
            <div className="section-content">
              <h1 className="section-title">{sessions.heading || "Sessions"}</h1>
              {sessions.intro ? <p>{sessions.intro}</p> : null}
              <dl className="accordion">
                {sessionItems.map((item, i) => {
                  const term = item.term;
                  return (
                    <React.Fragment key={item.id || i}>
                      <dt onClick={() => this.setSession(term)}>
                        <FontAwesomeIcon
                          className="icon"
                          icon={this.state.currSession === term ? faChevronDown : faChevronRight}
                        />
                        <span className="accordion-term">{term}</span>
                      </dt>
                      <dd className={this.state.currSession === term ? "accordion-descr" : "accordion-descr--collapsed"}>
                        {item.description}
                      </dd>
                    </React.Fragment>
                  );
                })}
              </dl>
            </div>
            <div id="sessions-splatter" className="section-image">
              <blockquote className="section-quote">
                {sessions.quote}
                <span className="section-author"></span>
              </blockquote>
            </div>
          </section>
        </React.Fragment>
      ),
      projects: () => (
        <React.Fragment key="projects">
          <span id="projects"></span>
          <section className="section project-section">
            <div className="section-content">
              <h1 className="section-title">{projects.heading || "Projects"}</h1>
              {projects.intro ? <p>{projects.intro}</p> : null}
              <ul className="grid-list project-grid" style={{ "--project-cols": Math.max(1, Math.ceil(projectItems.length / 2)) }}>
                {projectItems.map((p, i) => {
                  const selected = this.state.selectedProject === i;
                  return (
                    <li
                      key={p.id || i}
                      className={"grid-list-item project-grid-item" + (selected ? " selected" : "")}
                      role="button"
                      tabIndex={0}
                      aria-pressed={selected}
                      onClick={() => this.selectProject(i)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          this.selectProject(i);
                        }
                      }}
                    >
                      <img src={p.imageKey} alt={p.title} className="grid-list-image" />
                    </li>
                  );
                })}
              </ul>
              <div className="project-detail" aria-live="polite">
                {this.state.selectedProject !== null && projectItems[this.state.selectedProject] ? (
                  <div className="project-detail-card">
                    <div className="project-detail-name">{projectItems[this.state.selectedProject].title}</div>
                    <blockquote className="project-detail-caption">
                      {projectItems[this.state.selectedProject].captionOverride || projectItems[this.state.selectedProject].description}
                    </blockquote>
                  </div>
                ) : (
                  <div className="project-detail-hint">Click a project to learn more</div>
                )}
              </div>
            </div>

            <div id="projects-splatter" className="section-image">
              <blockquote className="section-quote">{projects.quote}</blockquote>
            </div>
          </section>
        </React.Fragment>
      ),
      leaders: () => (
        <React.Fragment key="leaders">
          <span id="leaders"></span>
          <section className="section leader-section">
            <div className="section-content">
              <h1 className="section-title">{leaders.heading || "Leaders"}</h1>
              <ul
                className="grid-list leader-grid"
                style={{ "--project-cols": Math.max(1, Math.ceil(leaderItems.length / 2)) }}
              >
                {leaderItems.map((p, i) => {
                  const selected = this.state.selectedLeader === i;
                  return (
                    <li
                      key={p.leaderId || i}
                      className={"grid-list-item leader-grid-item" + (selected ? " selected" : "")}
                      role="button"
                      tabIndex={0}
                      aria-pressed={selected}
                      onClick={() => this.selectLeader(i)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          this.selectLeader(i);
                        }
                      }}
                    >
                      <img src={p.headshotKey} alt={p.name} className="grid-list-image" />
                    </li>
                  );
                })}
              </ul>
              <div className="leader-detail" aria-live="polite">
                {this.state.selectedLeader !== null && leaderItems[this.state.selectedLeader] ? (
                  <div className="leader-detail-card">
                    <div className="leader-detail-name">{leaderItems[this.state.selectedLeader].name}</div>
                    <div className="leader-detail-title">{leaderItems[this.state.selectedLeader].title}</div>
                    <blockquote className="leader-detail-quote">{leaderItems[this.state.selectedLeader].quote}</blockquote>
                  </div>
                ) : (
                  <div className="leader-detail-hint">Click a leader to hear their thoughts</div>
                )}
              </div>
            </div>

            <div id="leaders-splatter" className="section-image">
              <blockquote className="section-quote">{leaders.quote}</blockquote>
            </div>
          </section>
        </React.Fragment>
      ),
    };

    return (
      <main className="main">
        <section id="about" className="section">
          {/* Background & Video */}
          <div
            id="about-splatter"
            className="about-splatter"
            style={{ opacity: hero.splatterOpacity ?? 1 }}
          ></div>
          <div id="about-video" className="about-video-container hidden" onClick={this.stopVideo}>
            <video
              src={hero.video || "/api/files/videos/CLEAR_Video.mp4"}
              className="about-video"
              controls
              onEnded={this.stopVideo}
              onClick={(e) => e.stopPropagation()}
            ></video>
          </div>

          {/* Section Content */}
          <div id="about-content" className="section-content about-section-text">
            <h1 className="about-title">
              <span className="about-title-text" style={{ opacity: hero.titleOpacity ?? 1 }}>
                {hero.title}
              </span>
              <br />
              <button id="about-video-button" className="video-button" onClick={this.startVideo}>
                <svg className="icon">
                  <use xlinkHref="#icon-play_circle_outline"></use>
                </svg>
              </button>
            </h1>
          </div>

          <div className="scroll-arrow-text">
            <span>
              <small>Scroll Down</small>
            </span>
          </div>
          <div className="scroll-arrow">
            <FontAwesomeIcon className="icon" icon={faChevronDown} />
          </div>
        </section>

        {sectionOrder.map((key) => sectionRenderers[key]())}

        <span id="apply"></span>
        <section className="section">
          <div className="section-content">
            <h1 className="section-title">{joinUs.heading || "Join Us!"}</h1>

            {activeMessage ? <p dangerouslySetInnerHTML={{ __html: activeMessage }} /> : null}

            <p>
              If you have any questions or comments, please reach out to us through{" "}
              {joinUs.slackUrl ? <a href={joinUs.slackUrl}>Slack</a> : null}
              {joinUs.contactEmail ? (
                <>
                  , or by Email:{" "}
                  <a href={`mailto:${joinUs.contactEmail}`}>{joinUs.contactEmail}</a>{" "}
                </>
              ) : null}
            </p>

            <div className="apply-actions">
              {activeState === "apply" ? (
                <a href="/apply" className="apply-button">
                  Apply Now
                </a>
              ) : null}
            </div>
          </div>

          <div id="apply-splatter" className="section-image">
            <blockquote className="section-quote">{joinUs.quote}</blockquote>
          </div>
        </section>
      </main>
    );
  }
}

export default Home;
